/**
 * Закрытие заявки на подбор (ТЗ-2026-011): анкета найденного кандидата — все 9 полей DTO
 * закрытия — плюс обязательный файл-резюме (КП-03). Лимиты полей зеркалят
 * close-recruitment.dto.ts. На нативе закрыть нельзя (в проекте нет document-picker) —
 * вместо формы подсказка про веб-версию.
 */
import { useState, type ComponentProps } from 'react'
import { Platform } from 'react-native'
import { Input, Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import { errorText } from '../errorText'
import { pickFileWeb } from '../../../lib/files'
import { tone } from '../../../theme/tones'
import { Button, DateField, Icon } from '../../../ui'
import type { CloseRecruitmentInput } from '../../../lib/types'
import { useCloseRecruitment } from '../useEdo'

/** Компактный инпут анкеты кандидата в форме закрытия — общий стиль полей. */
function CloseInput(props: ComponentProps<typeof Input>) {
  return (
    <Input
      height={36} fontSize={14} color="$color" backgroundColor="$bgLayout" borderWidth={1}
      borderColor="$borderColor" borderRadius={7} paddingHorizontal={11}
      placeholderTextColor="$tertiary" focusStyle={{ borderColor: '$primary' }}
      {...props}
    />
  )
}

export function CloseRecruitmentForm({ requestId, onDone }: { requestId: string; onDone: () => void }) {
  const { t } = useApp()
  const closeReq = useCloseRecruitment()
  const [candName, setCandName] = useState('')
  // Остальная анкета кандидата (ФТ-09, 9 полей DTO закрытия). Лимиты — close-recruitment.dto.ts.
  const [cand, setCand] = useState({ phone: '', email: '', position: '', salary: '', startDate: '', source: '', note: '' })
  const upCand = (patch: Partial<typeof cand>) => setCand((s) => ({ ...s, ...patch }))
  const [resume, setResume] = useState<File | null>(null)
  // Ошибка загрузки файла (upload идёт ДО мутации; её reject иначе терялся бы — аудит §3.4).
  const [uploadErr, setUploadErr] = useState('')

  const doClose = async () => {
    if (!resume || closeReq.isPending) return
    setUploadErr('')
    let resumeUploadId: string
    try {
      const form = new FormData()
      form.append('file', resume as unknown as Blob)
      resumeUploadId = (await api.uploadEdoDoc(form)).id
    } catch (e) {
      setUploadErr(t(errorText(e)))
      return
    }
    const data: CloseRecruitmentInput = {
      resumeUploadId,
      candidateFullName: candName.trim() || undefined,
      candidatePhone: cand.phone.trim() || undefined,
      candidateEmail: cand.email.trim() || undefined,
      candidatePosition: cand.position.trim() || undefined,
      agreedSalary: cand.salary ? Number(cand.salary) : undefined,
      candidateStartDate: cand.startDate || undefined,
      source: cand.source.trim() || undefined,
      note: cand.note.trim() || undefined,
    }
    closeReq.mutate({ id: requestId, data }, { onSuccess: onDone })
  }

  return (
    <>
      {Platform.OS !== 'web' ? (
        <Text fontSize={12} color="$muted">
          {t({
            ru: 'Закрытие вакансии (с прикреплением резюме) доступно в веб-версии портала',
            en: 'Closing the vacancy (with résumé upload) is available in the web version',
          })}
        </Text>
      ) : (
        <YStack gap={8}>
          <CloseInput
            value={candName}
            onChangeText={(v: string) => setCandName(v.slice(0, 200))}
            placeholder={t({ ru: 'ФИО кандидата', en: 'Candidate name' })}
          />
          <XStack gap={8} flexWrap="wrap">
            <CloseInput
              flexGrow={1} flexBasis={0} minWidth={140}
              value={cand.phone}
              onChangeText={(v: string) => upCand({ phone: v.slice(0, 50) })}
              placeholder={t({ ru: 'Телефон', en: 'Phone' })}
              keyboardType="phone-pad"
            />
            <CloseInput
              flexGrow={1} flexBasis={0} minWidth={140}
              value={cand.email}
              onChangeText={(v: string) => upCand({ email: v.slice(0, 200) })}
              placeholder="Email"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </XStack>
          <XStack gap={8} flexWrap="wrap">
            <CloseInput
              flexGrow={1} flexBasis={0} minWidth={140}
              value={cand.position}
              onChangeText={(v: string) => upCand({ position: v.slice(0, 200) })}
              placeholder={t({ ru: 'Предлагаемая должность', en: 'Offered position' })}
            />
            <CloseInput
              width={130}
              value={cand.salary}
              onChangeText={(v: string) => upCand({ salary: v.replace(/\D/g, '').slice(0, 9) })}
              placeholder={t({ ru: 'Зарплата', en: 'Salary' })}
              keyboardType="numeric"
            />
          </XStack>
          <XStack gap={8} flexWrap="wrap" alignItems="center">
            <Stack flexGrow={1} flexBasis={0} minWidth={150}>
              <DateField
                value={cand.startDate}
                onChange={(v) => upCand({ startDate: v })}
                placeholder={t({ ru: 'Дата выхода', en: 'Start date' })}
              />
            </Stack>
            <CloseInput
              flexGrow={1} flexBasis={0} minWidth={140}
              value={cand.source}
              onChangeText={(v: string) => upCand({ source: v.slice(0, 100) })}
              placeholder={t({ ru: 'Источник (HeadHunter…)', en: 'Source (LinkedIn…)' })}
            />
          </XStack>
          <CloseInput
            value={cand.note}
            onChangeText={(v: string) => upCand({ note: v.slice(0, 1000) })}
            placeholder={t({ ru: 'Примечание (этапы собеседований…)', en: 'Note (interview stages…)' })}
          />
          <Button
            variant="default"
            block
            icon={<Icon name="paperclip" size={15} color="$muted" />}
            onPress={async () => setResume(await pickFileWeb('.pdf,.doc,.docx'))}
          >
            {resume ? `📎 ${resume.name}` : t({ ru: 'Прикрепить резюме (PDF/DOC/DOCX)', en: 'Attach résumé (PDF/DOC/DOCX)' })}
          </Button>
          {!resume && (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Без файла-резюме закрыть заявку нельзя (КП-03)', en: 'A résumé file is required to close (KP-03)' })}
            </Text>
          )}
          <Button
            variant="primary"
            block
            disabled={!resume || closeReq.isPending}
            opacity={resume && !closeReq.isPending ? 1 : 0.5}
            icon={<Icon name="check" size={15} color="$onPrimary" />}
            onPress={() => void doClose()}
          >
            {closeReq.isPending
              ? t({ ru: 'Закрытие…', en: 'Closing…' })
              : t({ ru: 'Закрыть — кандидат найден', en: 'Close — candidate hired' })}
          </Button>
          {closeReq.error instanceof Error && (
            <Text fontSize={12} color={tone('red').text}>{t(errorText(closeReq.error))}</Text>
          )}
        </YStack>
      )}
      {uploadErr ? <Text fontSize={12} color={tone('red').text}>{uploadErr}</Text> : null}
    </>
  )
}
